import React, { useState, useEffect, useRef } from 'react';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import { Form } from 'react-final-form';
import { Card, Text, Button, Flex, Box } from '../../design-system';
import {
  composeValidators,
  required,
  emailVal,
  passwordVal,
} from '../../helpers/utils';
import FieldColumn from '../../components/FieldColumn';
import StyledLink from '../../components/StyledLink';
import config from '../../config';

const FormContainer = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const TooltipText = styled(Flex)`
  width: 100%;
  text-align: center;
  font-weight: normal;
  font-size: 16;
`;

const UserCard = styled(Card)`
  min-width: 600px;
`;

interface SignUpProps {
  onSubmit: (values: any) => void;
  initialValues?: any;
}

const SignUp = ({ onSubmit, initialValues }: SignUpProps) => {
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileWidgetId = useRef<any>(null);
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const win = window as any;
    if (
      win.turnstile &&
      config.TURNSTILE_SITE_KEY &&
      turnstileContainerRef.current
    ) {
      turnstileWidgetId.current = win.turnstile.render(
        turnstileContainerRef.current,
        {
          sitekey: config.TURNSTILE_SITE_KEY,
          callback: (token: string) => {
            setTurnstileToken(token);
          },
          'error-callback': () => {
            setTurnstileToken(null);
          },
          'expired-callback': () => {
            setTurnstileToken(null);
          },
        },
      );
    }

    return () => {
      if (turnstileWidgetId.current !== null && win.turnstile) {
        win.turnstile.remove(turnstileWidgetId.current);
      }
    };
  }, []);

  const handleSubmit = (values: any) => {
    onSubmit({ ...values, turnstile_token: turnstileToken });
  };

  return (
    <FormContainer mt={35}>
      <UserCard p={30} pt={0}>
        <Card.Header textAlign="center">
          <Text fontSize={18}>Thanks for trying out Grant Holster!</Text>
          <br />
          <Text fontSize={18} fontWeight="normal">
            To get you started, we just need some information from you. . .
          </Text>
        </Card.Header>

        <Card.Body mt={10} />

        <Form
          initialValues={initialValues}
          onSubmit={handleSubmit}
          render={({
            handleSubmit: formHandleSubmit,
            submitting,
            pristine,
            values,
          }) => (
            <form onSubmit={formHandleSubmit}>
              <FieldColumn name="name" label="Full Name" validate={required} />
              <FieldColumn
                name="organization"
                label="Organization"
                validate={required}
              />
              <FieldColumn
                name="email"
                label="Email"
                validate={composeValidators(required, emailVal)}
              />
              <FieldColumn
                name="password"
                label="Create Password"
                type="password"
                validate={composeValidators(required, passwordVal)}
              />
              <Box mt={20} mb={10}>
                <div ref={turnstileContainerRef} />
              </Box>
              <Button.Group
                justifyContent="space-between"
                alignItems="center"
                mt={30}
              >
                <StyledLink to="/login" color="blue">
                  <Text ml={1}>Login</Text>
                </StyledLink>
                <Button
                  mt={20}
                  mb={20}
                  minWidth={100}
                  type="submit"
                  disabled={
                    !turnstileToken ||
                    submitting ||
                    (isEmpty(values) && pristine)
                  }
                >
                  Next
                </Button>
              </Button.Group>
            </form>
          )}
        />
      </UserCard>
    </FormContainer>
  );
};

export default SignUp;
